import { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Download, ScanSearch, Trash2, AlertTriangle, Loader2, Check, FileText, Smartphone, KeyRound, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PII_TYPE_LABELS = {
  credit_card: 'Credit card',
  email: 'Email address',
  phone_intl: 'Phone (intl)',
  iban: 'IBAN',
  ssn_us: 'US SSN',
  cnic_pk: 'Pakistani CNIC',
  aadhaar_in: 'Indian Aadhaar',
  nino_uk: 'UK NINo',
};

const MfaSection = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  const load = async () => {
    try {
      const res = await axios.get(`${API}/auth/mfa/status`, { headers: { Authorization: `Bearer ${token}` } });
      setStatus(res.data);
    } catch {}
  };
  useEffect(() => { load(); }, [token]);

  const startSetup = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/mfa/setup`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSetup(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start MFA setup');
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    if (code.length !== 6) return toast.error('Enter the 6-digit code');
    setBusy(true);
    try {
      await axios.post(`${API}/auth/mfa/confirm`, { code },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('MFA enabled. Next login will require your authenticator code.');
      setSetup(null); setCode(''); await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (disableCode.length !== 6) return toast.error('Enter your current 6-digit code to disable');
    if (!window.confirm('Disable MFA on this account? Your login will be password-only after this.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auth/mfa/disable`, { code: disableCode },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('MFA disabled');
      setDisableCode(''); await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to disable MFA');
    } finally { setBusy(false); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="section-mfa">
      <div className="p-5 border-b border-slate-100 bg-emerald-50/40">
        <div className="flex items-start gap-3">
          <Smartphone size={22} className="text-emerald-600 flex-shrink-0 mt-1" />
          <div className="flex-1">
            <h2 className="text-lg font-heading font-semibold text-slate-900">
              Two-factor authentication (TOTP)
              {status?.enabled && (
                <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Enabled</span>
              )}
              {status?.required_by_role && !status?.enabled && (
                <span className="ml-2 text-xs bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full">Required by role</span>
              )}
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Add a second sign-in step using an authenticator app (Google Authenticator, 1Password, Authy).
              {status?.required_by_role && ' Admin role requires MFA and cannot be disabled.'}
            </p>
          </div>
        </div>
      </div>
      <div className="p-5 space-y-4">
        {!status?.enabled && !setup && (
          <Button onClick={startSetup} disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            data-testid="mfa-setup-button">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
            Set up authenticator
          </Button>
        )}
        {setup && (
          <div className="space-y-3" data-testid="mfa-setup-panel">
            <p className="text-sm text-slate-700">Scan this QR code with your authenticator app:</p>
            <img src={setup.qr_png_data_url} alt="MFA QR" className="border border-slate-200 rounded p-2 bg-white" data-testid="mfa-qr" />
            <p className="text-xs text-slate-500">
              Or enter this secret manually: <code className="px-2 py-0.5 bg-slate-100 rounded font-mono text-slate-800" data-testid="mfa-secret">{setup.secret}</code>
            </p>
            <div className="flex items-center gap-2">
              <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit code"
                className="w-40 px-3 py-2 border border-slate-300 rounded-sm text-lg font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="mfa-confirm-input" />
              <Button onClick={confirm} disabled={busy || code.length !== 6}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="mfa-confirm-button">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                Verify & enable
              </Button>
              <Button variant="outline" onClick={() => { setSetup(null); setCode(''); }}
                data-testid="mfa-cancel-button">Cancel</Button>
            </div>
          </div>
        )}
        {status?.enabled && !status?.required_by_role && (
          <div className="flex items-center gap-2" data-testid="mfa-disable-panel">
            <input value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit code"
              className="w-40 px-3 py-2 border border-slate-300 rounded-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-rose-500"
              data-testid="mfa-disable-input" />
            <Button variant="outline" onClick={disable} disabled={busy}
              className="border-rose-400 text-rose-700 hover:bg-rose-50 gap-2" data-testid="mfa-disable-button">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <X size={16} />}
              Disable MFA
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export const PrivacyAndData = ({ token, user }) => {
  const [sessions, setSessions] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [masking, setMasking] = useState(false);
  const [scanUploadId, setScanUploadId] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/spend/processing-sessions`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      const list = res.data.sessions || [];
      setSessions(list);
      if (list.length > 0) setScanUploadId(list[0].id);
    }).catch(() => { /* silent */ });
  }, [token]);

  const CONFIRM_PHRASE = 'DELETE MY ACCOUNT AND ALL DATA';

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/gdpr/my-data-export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.download = `datarefinery_export_${stamp}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Data export downloaded. Contains every record we hold about you.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleScan = async () => {
    if (!scanUploadId) return toast.error('Select a session first');
    setScanning(true);
    setScanResult(null);
    try {
      const res = await axios.post(`${API}/gdpr/pii-scan/${scanUploadId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setScanResult(res.data);
      if (res.data.total_pii_hits === 0) {
        toast.success(`No PII detected across ${res.data.records_scanned} records`);
      } else {
        toast.info(`Found ${res.data.total_pii_hits} potential PII hits`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleMask = async () => {
    if (!scanResult || scanResult.total_pii_hits === 0) return;
    if (!window.confirm(
      `Redact all ${scanResult.total_pii_hits} PII hits across ${scanResult.records_scanned} records?\n\n` +
      'Original values will be backed up (reversible via support). Records will show [REDACTED_TYPE] placeholders.'
    )) return;
    setMasking(true);
    try {
      const res = await axios.post(`${API}/gdpr/pii-mask/${scanUploadId}`,
        { confirm: true },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Masked PII in ${res.data.records_masked} records. Backup retained.`);
      setScanResult(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Masking failed');
    } finally {
      setMasking(false);
    }
  };

  const handleDelete = async () => {
    if (deleteInput !== CONFIRM_PHRASE) {
      toast.error(`Type exactly: ${CONFIRM_PHRASE}`);
      return;
    }
    setDeleting(true);
    try {
      const res = await axios.post(`${API}/gdpr/erasure-request`,
        { confirm: CONFIRM_PHRASE },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Account erased. ${res.data.total_records_deleted} records deleted. Tombstone: ${res.data.tombstone_id.slice(0, 8)}`);
      setTimeout(() => { window.location.href = '/login'; }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erasure failed');
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="privacy-and-data-page">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Shield size={28} className="text-blue-600" />
          <h1 className="text-3xl font-heading font-bold text-[#0F172A]">Privacy & Data</h1>
        </div>
        <p className="text-slate-600">
          Exercise your data rights under GDPR (Articles 15, 17, 20). Everything on this page is
          scoped to your account only.
        </p>
      </div>

      <MfaSection token={token} />

      {/* Right to Access — Data Export */}      <section className="bg-white border border-slate-200 rounded-sm overflow-hidden"
               data-testid="section-export">
        <div className="p-5 border-b border-slate-100 bg-blue-50/40">
          <div className="flex items-start gap-3">
            <Download size={22} className="text-blue-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h2 className="text-lg font-heading font-semibold text-slate-900">Download my data</h2>
              <p className="text-sm text-slate-600 mt-1">
                Downloads a ZIP with every record, correction, rule, connector, audit entry, and
                classification result linked to your account — in CSV and JSON. Fulfils GDPR
                Article 15 (Access) & Article 20 (Portability).
              </p>
            </div>
          </div>
        </div>
        <div className="p-5">
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            data-testid="download-my-data-button"
          >
            {exporting ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
            {exporting ? 'Preparing archive…' : 'Download my data (.zip)'}
          </Button>
        </div>
      </section>

      {/* PII Scanner */}
      <section className="bg-white border border-slate-200 rounded-sm overflow-hidden"
               data-testid="section-pii-scan">
        <div className="p-5 border-b border-slate-100 bg-amber-50/40">
          <div className="flex items-start gap-3">
            <ScanSearch size={22} className="text-amber-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h2 className="text-lg font-heading font-semibold text-slate-900">PII scanner</h2>
              <p className="text-sm text-slate-600 mt-1">
                Scan a session&apos;s supplier and description fields for personally identifiable
                information (credit cards, emails, phone numbers, national IDs). Redact detected
                PII with one click — backups are retained.
              </p>
            </div>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-slate-700 flex-shrink-0">Session:</label>
            <select
              value={scanUploadId}
              onChange={(e) => { setScanUploadId(e.target.value); setScanResult(null); }}
              className="flex-1 px-3 py-2 border border-slate-200 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="pii-scan-session-select"
            >
              <option value="">— pick a session —</option>
              {sessions.map(s => (
                <option key={s.id} value={s.id}>{s.filename} ({s.total_records} records)</option>
              ))}
            </select>
            <Button
              onClick={handleScan}
              disabled={scanning || !scanUploadId}
              className="bg-amber-600 hover:bg-amber-700 text-white gap-2 flex-shrink-0"
              data-testid="pii-scan-button"
            >
              {scanning ? <Loader2 size={16} className="animate-spin" /> : <ScanSearch size={16} />}
              {scanning ? 'Scanning…' : 'Scan for PII'}
            </Button>
          </div>

          {scanResult && (
            <div className="border border-slate-200 rounded-sm p-4 bg-slate-50" data-testid="pii-scan-result">
              <div className="flex items-baseline justify-between mb-3">
                <div>
                  <p className="text-sm text-slate-600">
                    Scanned <strong>{scanResult.records_scanned.toLocaleString()}</strong> records.
                  </p>
                  <p className={`text-lg font-heading font-semibold ${scanResult.total_pii_hits > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                    {scanResult.total_pii_hits === 0
                      ? '✓ No PII detected'
                      : `${scanResult.total_pii_hits} potential PII hits`}
                  </p>
                </div>
                {scanResult.total_pii_hits > 0 && (
                  <Button
                    onClick={handleMask}
                    disabled={masking}
                    className="bg-rose-600 hover:bg-rose-700 text-white gap-2"
                    data-testid="pii-mask-button"
                  >
                    {masking ? <Loader2 size={16} className="animate-spin" /> : <Shield size={16} />}
                    Redact all
                  </Button>
                )}
              </div>
              {scanResult.total_pii_hits > 0 && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-3">
                    {Object.entries(scanResult.hits_by_type).map(([t, c]) => (
                      <div key={t} className="bg-white border border-slate-200 rounded-sm p-2">
                        <div className="text-slate-500">{PII_TYPE_LABELS[t] || t}</div>
                        <div className="text-slate-900 font-heading font-semibold">{c}</div>
                      </div>
                    ))}
                  </div>
                  <details className="text-xs">
                    <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
                      Show first {Math.min(scanResult.examples.length, 10)} example(s)
                    </summary>
                    <div className="mt-2 space-y-1 max-h-48 overflow-y-auto" data-testid="pii-scan-examples">
                      {scanResult.examples.slice(0, 10).map((e, i) => (
                        <div key={i} className="bg-white border border-slate-200 rounded-sm p-2 flex items-center gap-3">
                          <span className="text-amber-700 font-mono flex-shrink-0">{PII_TYPE_LABELS[e.type] || e.type}</span>
                          <span className="text-slate-500 flex-shrink-0">in {e.field}</span>
                          <code className="text-slate-700 font-mono flex-1 truncate" title={e.context}>{e.context}</code>
                        </div>
                      ))}
                    </div>
                  </details>
                </>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Right to Erasure */}
      <section className="bg-white border border-rose-200 rounded-sm overflow-hidden"
               data-testid="section-erasure">
        <div className="p-5 border-b border-rose-100 bg-rose-50/40">
          <div className="flex items-start gap-3">
            <Trash2 size={22} className="text-rose-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h2 className="text-lg font-heading font-semibold text-slate-900">Delete my account</h2>
              <p className="text-sm text-slate-600 mt-1">
                Irreversibly erases your account and every record we hold about you. Fulfils GDPR
                Article 17 (Right to Erasure). A tombstone entry with no PII will be retained for
                compliance proof.
              </p>
            </div>
          </div>
        </div>
        <div className="p-5">
          {!deleteConfirm ? (
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm(true)}
              className="border-rose-400 text-rose-700 hover:bg-rose-50 gap-2"
              data-testid="start-erasure-button"
            >
              <AlertTriangle size={16} />
              I want to delete my account
            </Button>
          ) : (
            <div className="border border-rose-300 rounded-sm p-4 bg-rose-50/60 space-y-3" data-testid="erasure-confirm-box">
              <div className="flex items-start gap-2 text-sm text-rose-900">
                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">This cannot be undone.</p>
                  <p className="text-xs mt-1">
                    All spend records, corrections, rules, connectors, taxonomies, and audit
                    entries linked to <strong>{user?.email || 'your account'}</strong> will be
                    permanently deleted. Any exports or downstream apps (like SpendLens) will lose
                    their source. If you&apos;ve been invited by an organisation, coordinate with your
                    admin first.
                  </p>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Type <code className="px-1 bg-white border border-slate-200 rounded">{CONFIRM_PHRASE}</code> to confirm:
                </label>
                <input
                  value={deleteInput}
                  onChange={(e) => setDeleteInput(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono focus:outline-none focus:ring-2 focus:ring-rose-500"
                  placeholder={CONFIRM_PHRASE}
                  data-testid="erasure-confirm-input"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => { setDeleteConfirm(false); setDeleteInput(''); }}
                  disabled={deleting}
                  data-testid="erasure-cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={deleting || deleteInput !== CONFIRM_PHRASE}
                  className="bg-rose-600 hover:bg-rose-700 text-white gap-2"
                  data-testid="erasure-execute-button"
                >
                  {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  {deleting ? 'Deleting…' : 'Permanently delete my account'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      <div className="text-xs text-slate-500 flex items-center gap-2 pt-4 border-t border-slate-100">
        <Check size={12} />
        Every action on this page is logged with a signed audit entry for compliance proof.
      </div>
    </div>
  );
};
